import React, { useEffect, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import { Edit, Trash, Eye as ExternalLink } from "lucide-react";

const BlogTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const token = sessionStorage.getItem("accesstoken");
  const role = sessionStorage.getItem("role");

  // Fetch data from the API
  const fetchData = async () => {
    try {
      const response = await fetch(
        "https://rohan-tax-services.onrender.com/api/auth/fetchblog",
        {
          method: "POST",
        }
      );
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching blog data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const categories = [
    "Income Tax",
    "TaxUpdates NG",
    "GST (Goods and Services Tax)",
    "Professional Tax",
    "TDS (Tax Deducted at Source)",
    "TCS (Tax Collected at Source)",
  ];

  // Handle Delete
  const handleDelete = async () => {
    try {
      await fetch(
        `https://rohan-tax-services.onrender.com/api/auth/deleteblog`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `${token}`,
          },
          body: JSON.stringify({ id: deleteId, role: role }),
        }
      );
      setData(data.filter((item) => item._id !== deleteId));
      fetchData();
    } catch (error) {
      console.error("Error deleting blog:", error);
    } finally {
      setDeleteId(null);
    }
  };

  // Handle Edit Save
  const handleEditSave = async () => {
    try {
      const updatedEditData = { ...editData, role: role };
      const response = await fetch(
        `https://rohan-tax-services.onrender.com/api/auth/updateblog`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(updatedEditData),
        }
      );
      const updatedBlog = await response.json();
      setData((prevData) =>
        prevData.map((item) =>
          item._id === updatedBlog._id ? updatedBlog : item
        )
      );
      fetchData();
    } catch (error) {
      console.error("Error updating blog:", error);
    } finally {
      setEditData(null);
    }
  };

  // Define columns for the table
  const columns = [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "date",
      header: "Date",
    },
    {
      accessorKey: "like",
      header: "Likes",
    },
    {
      accessorKey: "image",
      header: "Image",
      Cell: ({ cell }) => (
        <img
          src={cell.getValue()}
          alt="Blog"
          style={{ width: "100px", height: "auto", borderRadius: "8px" }}
        />
      ),
    },
    {
      accessorKey: "Preview",
      header: "More Info",
      size: 100,
      Cell: ({ cell }) => (
        <IconButton
          style={{ color: "green" }}
          onClick={() => setPreviewData(cell.row.original)}
        >
          <ExternalLink />
        </IconButton>
      ),
    },
    {
      accessorKey: "Edit",
      header: "Edit",
      size: 100,
      Cell: ({ cell }) => (
        <IconButton
          style={{ color: "blue" }}
          onClick={() => setEditData(cell.row.original)}
        >
          <Edit />
        </IconButton>
      ),
    },
    {
      accessorKey: "Delete",
      header: "Delete",
      size: 100,
      Cell: ({ cell }) => (
        <IconButton
          style={{ color: "red" }}
          onClick={() => setDeleteId(cell.row.original._id)}
        >
          <Trash />
        </IconButton>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <MaterialReactTable
        columns={columns}
        data={data}
        state={{ isLoading: loading }}
      />

      {/* Preview Dialog */}
      {previewData && (
        <Dialog open onClose={() => setPreviewData(null)}>
          <DialogTitle>More Info</DialogTitle>
          <DialogContent>
            <Typography variant="h6">Author: {previewData.author}</Typography>
            <Typography variant="h6">Source: {previewData.source}</Typography>
            <Typography variant="h6">
              Keywords: {previewData.keywords}
            </Typography>
            <Typography variant="h6">Description:</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {previewData.description}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewData(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {/* Edit Dialog */}
      {editData && (
        <Dialog
          open
          onClose={() => setEditData(null)}
          fullWidth
          maxWidth={false} // Allows setting custom width
          PaperProps={{
            sx: {
              width: { xs: "90vw", sm: "80vw", md: "60vw" }, // Responsive width
              height: { xs: "60vh", sm: "90vh" }, // Responsive height
              maxWidth: "none", // Remove default max width restriction
              borderRadius: { xs: 1, sm: 2 }, // Responsive border-radius
            },
          }}
        >
          <DialogTitle>Edit Blog</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 2,
                pt: 2,
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <TextField
                label="Title"
                value={editData.title}
                onChange={(e) =>
                  setEditData({ ...editData, title: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="Source"
                value={editData.source}
                onChange={(e) =>
                  setEditData({ ...editData, source: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="Category"
                value={editData.category}
                select
                onChange={(e) =>
                  setEditData({ ...editData, category: e.target.value })
                }
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Description"
                value={editData.description}
                onChange={(e) =>
                  setEditData({ ...editData, description: e.target.value })
                }
                multiline
                rows={8}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditData(null)}>Cancel</Button>
            <Button
              onClick={handleEditSave}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteId && (
        <Dialog open onClose={() => setDeleteId(null)}>
          <DialogTitle>Delete Blog</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this blog?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default BlogTable;
